import { sha1 } from 'object-hash';
import { Currency, Locale } from '../enums/currency';
import { Numberish, Stringish } from '../enums/utilities';
import { DateUtil } from './date';

const MIN_PWD_LENGTH = 6;

export class StringUtil {
  static titlecase(value: string, delimiter: string): string {
    const words = value.split(delimiter);

    const capped = words.map((word) =>
      word
        .split('')
        .map((char, i) => (i === 0 ? char.toUpperCase() : char.toLowerCase()))
    );

    return capped.join(' ');
  }

  static getDomain(email: string): string {
    const lastAt = email.lastIndexOf('@');

    if (lastAt === -1) {
      return 'NO DOMAIN';
    }

    return email.substring(lastAt + 1);
  }

  static validPassword(password: Stringish): {
    valid: boolean;
    reason?: string;
  } {
    if (!password) {
      return { valid: false, reason: 'Password cannot be empty' };
    }

    if (password.length < MIN_PWD_LENGTH) {
      return {
        valid: false,
        reason: `Password cannot be less than ${MIN_PWD_LENGTH} characters`,
      };
    }

    // todo: implement complexity checks

    return { valid: true };
  }

  static printDate = DateUtil.printDate;
  static printDateTime = DateUtil.printDateTime;

  // replaces each char of a string with
  static censor(
    value: Stringish,
    options?: {
      replacement?: string;
      keepHead?: number;
      keepTail?: number;
    }
  ) {
    return (value ?? '')
      .split('')
      .map((c, i) => {
        if (!value) {
          // shouldn't trigger
          return '';
        }

        if (options?.keepHead !== undefined && i <= options.keepHead) {
          // this char is near the start fo the string
          return c;
        }

        if (
          options?.keepTail !== undefined &&
          i >= value.length - options.keepTail
        ) {
          // this char is near the end of the string
          return c;
        }

        // replace every remaining char with the replacement string
        return options?.replacement ?? '*';
      })
      .join('');
  }

  static hashObj = (input: any): string | undefined => {
    try {
      if (typeof input === 'object') {
        return sha1(JSON.stringify(input));
      }

      if (typeof input === 'string') {
        return sha1(input);
      }

      /** attempt a dirty conversion */
      return sha1(input ?? '');
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };

  // numeric value if its a number; undefined otherwise
  static tryParseNumber = (value: Numberish | string): number | undefined => {
    if (value === undefined) {
      return undefined;
    }

    if (value === null) {
      return undefined;
    }

    const output = Number(value);

    if (isNaN(output)) {
      return undefined;
    }

    return output;
  };

  static printMoney(
    amount: Numberish,
    decimals = 2,
    currency = Currency.CAD,
    locale = Locale.enCA
  ): string {
    const safeAmount = StringUtil.tryParseNumber(amount);

    if (safeAmount === undefined) {
      return '';
    }

    const isNegative = safeAmount < 0;

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    const formatted = formatter.format(Math.abs(safeAmount));
    return isNegative ? `(${formatted})` : formatted;
  }

  // aka: number of security
  static printUnits(
    amount: Numberish,
    decimals = 2,
    locale = Locale.enCA
  ): string {
    const safeAmount = StringUtil.tryParseNumber(amount);

    if (safeAmount === undefined) {
      return '';
    }

    const formatter = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return formatter.format(safeAmount);
  }

  static printPercent(
    amount: Numberish,
    decimals = 2,
    locale = Locale.enCA
  ): string {
    const safeAmount = StringUtil.tryParseNumber(amount);

    if (safeAmount === undefined) {
      return '';
    }

    const formatter = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return formatter.format(safeAmount);
  }
}
